"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Carousel } from "antd";
import { useIntl } from "react-intl";
import "./card-slider.css";

interface CommonCardSliderProps<T> {
  title: string;
  subTitle?: string;
  items: T[];
  slidesPerPage?: number;
  responsive?: ResponsiveSetting[];
  showDots?: boolean;
  showArrows?: boolean;
  gap?: number;
  cardMinWidth?: number;
  cardMaxWidth?: number | string;
  onSlideChange?: (currentSlide: number) => void;
  emptyState?: React.ReactNode;
  getKey?: (item: T, index: number) => React.Key;
  className?: string;
  noRecordText?: string;
  renderItem: (item: T, index: number) => React.ReactNode;
};

type LayoutDirection = "ltr" | "rtl";

interface ResponsiveSetting {
  maxWidth: number;
  slidesToShow: number;
}

interface DisplayItem<T> {
  value: T | null;
  originalIndex: number;
  isPlaceholder: boolean;
}

const DEFAULT_DESKTOP_SLIDES = 3;
const DEFAULT_BREAKPOINTS: ResponsiveSetting[] = [
  { maxWidth: 767, slidesToShow: 1 },
  { maxWidth: 1023, slidesToShow: 2 },
];

const buildResponsiveSettings = (
  settings: ResponsiveSetting[] | undefined,
  maxSlides: number
): ResponsiveSetting[] => {
  const source = settings?.length ? settings : DEFAULT_BREAKPOINTS;

  return source
    .filter(({ slidesToShow }) => slidesToShow > 0)
    .map((setting) => ({
      ...setting,
      slidesToShow: Math.max(1, Math.min(setting.slidesToShow, maxSlides)),
    }))
    .sort((a, b) => a.maxWidth - b.maxWidth);
};

const CommonCardSlider = <T,>({
  title,
  subTitle = "",
  items,
  slidesPerPage = 3,
  responsive,
  showDots,
  showArrows,
  gap = 20,
  cardMinWidth,
  cardMaxWidth,
  onSlideChange,
  emptyState,
  getKey,
  className,
  renderItem,
  noRecordText,
}: CommonCardSliderProps<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [containerWidth, setContainerWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const intl = useIntl();

  const direction: LayoutDirection = intl.locale === "ar" ? "rtl" : "ltr";

  const effectiveSlidesPerView = useMemo(() => {
    const base = slidesPerPage ?? DEFAULT_DESKTOP_SLIDES;
    return Math.max(1, base);
  }, [slidesPerPage]);

  const normalizedResponsive = useMemo(
    () => buildResponsiveSettings(responsive, effectiveSlidesPerView),
    [effectiveSlidesPerView, responsive]
  );

  const slidesToShow = useMemo(() => {
    if (items.length === 0) {
      return 1;
    }

    if (cardMinWidth && cardMinWidth > 0) {
      const widthBasis = containerWidth || windowWidth;
      const computed = Math.max(1, Math.floor(widthBasis / cardMinWidth));
      return Math.max(1, Math.min(computed, effectiveSlidesPerView));
    }

    for (const setting of normalizedResponsive) {
      if (windowWidth <= setting.maxWidth) {
        return Math.max(1, Math.min(setting.slidesToShow, effectiveSlidesPerView));
      }
    }

    return effectiveSlidesPerView;
  }, [cardMinWidth, containerWidth, effectiveSlidesPerView, items.length, normalizedResponsive, windowWidth]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    const updateWidth = () => {
      const width = node.getBoundingClientRect().width;
      if (width > 0) {
        setContainerWidth(width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        if (!entries.length) {
          return;
        }
        const { contentRect } = entries[0];
        if (contentRect.width > 0) {
          setContainerWidth(contentRect.width);
        }
      });
      observer.observe(node);
      return () => observer.disconnect();
    }

    updateWidth();
  }, []);

  const displayItems = useMemo(() => {
    if (items.length === 0) {
      return [] as DisplayItem<T>[];
    }
    const totalSlides = Math.max(items.length, slidesToShow);
    const placeholderCount = totalSlides - items.length;

    const realItems = items.map((value, index): DisplayItem<T> => ({
      value,
      originalIndex: index,
      isPlaceholder: false,
    }));
    const placeholders = Array.from(
      { length: placeholderCount },
      (_, index): DisplayItem<T> => ({
        value: null,
        originalIndex: items.length + index,
        isPlaceholder: true,
      })
    );

    // react-slick reverses slide order in RTL, so trailing placeholders would
    // land on the leading (right) edge and push the real cards to the left.
    // Prepend them in RTL so the empty slots stay on the left and the cards
    // hug the right; keep them trailing in LTR.
    return direction === "rtl"
      ? [...placeholders, ...realItems]
      : [...realItems, ...placeholders];
  }, [direction, items, slidesToShow]);

  const shouldShowArrows = typeof showArrows === "boolean"
    ? showArrows
    : displayItems.length > slidesToShow;

  const resolvedShowDots = typeof showDots === "boolean"
    ? showDots
    : displayItems.length > slidesToShow;

  const carouselSettings = useMemo(
    () => ({
      dots: resolvedShowDots,
      arrows: shouldShowArrows,
      slidesToShow,
      slidesToScroll: slidesToShow,
      infinite: false,
      rtl: direction === "rtl",
      draggable: displayItems.length > slidesToShow,
    }),
    [direction, displayItems.length, resolvedShowDots, shouldShowArrows, slidesToShow]
  );

  const slidePaddingStyle = { paddingLeft: gap / 2, paddingRight: gap / 2 };

  const resolvedCardMaxWidth = typeof cardMaxWidth === "number"
    ? `${cardMaxWidth}px`
    : cardMaxWidth;

  const slideAlignStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: direction === "rtl" ? "flex-end" : "flex-start",
    height: "100%",
    width: "100%",
  };

  const cardContainerStyle: React.CSSProperties = {
    ...slidePaddingStyle,
    flex: "0 0 auto",
    width: "100%",
    minWidth: cardMinWidth,
    maxWidth: resolvedCardMaxWidth,
  };

  const handleAfterChange = (current: number) => {
    onSlideChange?.(current);
  };

  const containerClassName = [
    "common_card_slider",
    "thumbnail_slider",
    items.length > 0 && !shouldShowArrows ? "common_card_slider--center" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={containerClassName}
      dir={direction}
      aria-live="polite"
      ref={containerRef}
    >
      <div>
        {/* The wrapper div always renders — its 20px margin is load-bearing
            spacing for title="" callers whose dots/arrows are absolutely
            positioned against it. Only the headings are conditional, so an
            empty <h2> never lands in the accessibility tree. */}
        <div className="title">
          {title && <h2>{title}</h2>}
          {/* h3 (not h6) so headings descend sequentially under the h2 —
              fixes the `heading-order` accessibility audit. Styling unchanged. */}
          {subTitle && <h3>{subTitle}</h3>}
        </div>

        {items.length === 0 ? (
          <div className="d-flex flex-col align-items-center justify-content-center gap-8">
            <p>{noRecordText ?? intl.formatMessage({ id: "noResult" })}</p>
          </div>
        ) : (
          <Carousel
            className="carousel"
            {...carouselSettings}
            afterChange={handleAfterChange}
          >
            {displayItems.map(({ value, originalIndex, isPlaceholder }, index) => {
              const key = value
                ? getKey?.(value, originalIndex) ?? originalIndex
                : `placeholder-${index}`;

              return (
                <div
                  key={key}
                  className="common-card-slider__slide"
                  style={slideAlignStyle}
                >
                  <div
                    style={{
                      ...cardContainerStyle,
                      visibility: isPlaceholder ? "hidden" : "visible",
                    }}
                  >
                    {!isPlaceholder && value !== null
                      ? renderItem(value, originalIndex)
                      : null}
                  </div>
                </div>
              );
            })}
          </Carousel>
        )}
      </div>
    </div>
  );
};

export default CommonCardSlider;
